import MobileOTPLogin from "@/apps/los/components/MobileOTPLogin/MobileOTPLogin";
import BrandedJourneyHeader from "@/components/BrandedJourneyHeader";
import { API_BASE_URL } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import axios, { AxiosResponse } from "axios";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  IndianRupee,
  LogOut,
  Phone,
  RefreshCw,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

enum Step {
  Login,
  LoadingDetails,
  Details,
  NoDetails,
}

interface UserDetails {
  first_name: string;
  last_name: string;
}

interface LoanDetails {
  lender_name: string;
  account_number: string;
  tenure: number;
  payment_freq: string;
  installment_paid_cnt: number;
  installment_left_cnt: number;
  status_date: string;
  disbursed_at: string;
  loan: string;
  disbursed_amount: number;
  pos_amount: number;
  total_overdue_amount: number;
  days_past_due: number;
}

interface BasicLoanDetailsResponse {
  user_details: UserDetails;
  loan_details: LoanDetails;
}

const FetchBasicLoanDetails = (): Promise<
  AxiosResponse<BasicLoanDetailsResponse>
> => {
  return axios.get(`${API_BASE_URL}/api/basic_loan_details/`);
};

const RequestSupport = (loanId: string): Promise<AxiosResponse<any>> => {
  return axios.post(`${API_BASE_URL}/api/basic_loan_details/need_help/`, {
    loan_id: loanId,
  });
};

const BasicLoanDetailsPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(Step.Login);
  // const [details, setDetails] = useState<BasicLoanDetailsResponse>({
  //   user_details: {
  //     first_name: "Rameshwar",
  //     last_name: "Kamble",
  //   },
  //   loan_details: {
  //     lender_name: "Caprion",
  //     tenure: {
  //       days_paid: 30,
  //       days_left: 15,
  //       total_days: 45,
  //     },
  //     disbursed_amount: 500000.0,
  //     // pos_amount: 357269.0,
  //     total_overdue_amount: 3000.0,
  //     principal_overdue_amount: 2000.0,
  //     interest_overdue_amount: 1000.0,
  //     fee_due_amount: 0.0,
  //     status_date: "2026-09-24",
  //     disbursed_at: "2026-07-24T00:00:00Z",
  //     days_past_due: 3,
  //     loan: "INTELLEND9500",
  //   },
  // });

  const [details, setDetails] = useState<BasicLoanDetailsResponse>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSupportRequesting, setIsSupportRequesting] = useState(false);
  const [isSupportSuccess, setIsSupportSuccess] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");

  const getPaidPrincipalAmount = (loanDetails: LoanDetails): number => {
    return loanDetails.disbursed_amount - loanDetails.pos_amount;
  };

  const getTenureUnitLabel = (paymentFreq: string): string => {
    switch (paymentFreq) {
      case "Weekly":
        return "Weeks";
      case "Monthly":
        return "Months";
      default:
        return "Days";
    }
  };

  const getPaidPrincipalPercentage = (loanDetails: LoanDetails): number => {
    return (
      (getPaidPrincipalAmount(loanDetails) / loanDetails.disbursed_amount) * 100
    );
  };

  const isOverDue = (loanDetails: LoanDetails): boolean => {
    return (
      loanDetails.total_overdue_amount > 0 || loanDetails.days_past_due > 0
    );
  };

  const fetchLoanDetails = async () => {
    setCurrentStep(Step.LoadingDetails);
    let responseDetails: BasicLoanDetailsResponse;
    try {
      let response = await FetchBasicLoanDetails();
      responseDetails = response.data;
    } catch (e) {
      setErrorMessage(e.message);
      setCurrentStep(Step.NoDetails);
      return;
    }
    setDetails(responseDetails);
    if (responseDetails.loan_details) {
      setCurrentStep(Step.Details);
    } else {
      setErrorMessage(
        "We couldn't find any loan information associated with your account.",
      );
      setCurrentStep(Step.NoDetails);
    }
  };

  const onLoginError = (message: string) => {
    console.error(message);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const handleRequestSupport = async () => {
    setIsSupportRequesting(true);
    const loanId = details.loan_details.loan;
    let supportResponse: AxiosResponse<any>;
    try {
      supportResponse = await RequestSupport(loanId);
    } catch (e) {
      setIsSupportSuccess(false);
      setSupportMessage(e.message);
      setIsSupportRequesting(false);
      return;
    }
    if (supportResponse.status != 200) {
      setIsSupportSuccess(false);
      setIsSupportRequesting(false);
      setSupportMessage("Failed to request support. Please try again later.");
    } else {
      setIsSupportSuccess(true);
      setSupportMessage("Intellend team will reach you soon.");
    }
  };

  const logoutUser = () => {
    logout();
    setCurrentStep(Step.Login);
    setDetails(null);
    setErrorMessage("");
    setIsSupportRequesting(false);
    setSupportMessage("");
    navigate("/basic-loan-details");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {currentStep == Step.Login && (
        <>
          <div className="bg-intellend-green">
            <BrandedJourneyHeader />
          </div>
          <div className="flex-1 bg-gray-50 px-4 py-6">
            <div className="w-full max-w-md mx-auto bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                Login to Continue
              </h2>
              <p className="text-sm md:text-base text-gray-600 mb-6">
                Enter your mobile number to proceed with your loan application
              </p>
              <MobileOTPLogin
                onNext={fetchLoanDetails}
                onSuccess={fetchLoanDetails}
                onError={onLoginError}
              />
            </div>
          </div>
        </>
      )}
      {currentStep == Step.LoadingDetails && (
        <div className="min-h-screen bg-gradient-to-br from-intellend-green to-intellend-green/80 flex items-center justify-center">
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-white animate-spin"></div>
              <div
                className="absolute inset-2 rounded-full border-4 border-transparent border-t-intellend-gold animate-spin"
                style={{ animationDuration: "1.5s" }}
              ></div>
            </div>
            <p className="text-white text-sm font-medium">Loading...</p>
          </div>
        </div>
      )}
      {currentStep == Step.Details && (
        <div className="flex min-h-screen flex-col bg-gray-50">
          {/* Header */}
          <header className="border-b border-gray-200 bg-white">
            <div className="mx-auto flex h-16 w-full max-w-5xl items-center px-6">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Loan Details
                </h1>

                <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                  <Clock3 className="h-3.5 w-3.5" />
                  <span>
                    Last updated on{" "}
                    {formatDate(details.loan_details.status_date)} 00:00 AM.
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1">
            <div className="mx-auto w-full max-w-5xl px-6 py-6">
              {/* Borrower + Loan */}
              <div className="mb-6 grid gap-6 md:grid-cols-2">
                {/* Borrower */}
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <p className="text-lg font-semibold text-gray-900">
                    {details.user_details.first_name}{" "}
                    {details.user_details.last_name}
                  </p>

                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500">Loan ID</span>
                      <span className="font-medium text-gray-900">
                        {details.loan_details.loan}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500">Lender</span>
                      <span className="font-medium text-gray-900">
                        {details.loan_details.lender_name}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500">Account Number</span>
                      <span className="font-medium text-gray-900">
                        {details.loan_details.account_number}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-500">Disbursed On</span>
                      <span className="font-medium text-gray-900">
                        {formatDate(details.loan_details.disbursed_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Loan */}
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Loan
                    </h3>

                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                      <IndianRupee className="h-4 w-4" />
                    </div>
                  </div>

                  {/* Loan */}
                  <div>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-1.5 text-gray-500">Loan Amount</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">
                            {formatCurrency(
                              details.loan_details.disbursed_amount,
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-1.5 text-gray-500">Paid</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">
                            {formatCurrency(
                              getPaidPrincipalAmount(details.loan_details),
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-1.5 text-gray-500">Outstanding</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">
                            {formatCurrency(details.loan_details.pos_amount)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Repayment Progress Bar */}
                  <div className="mt-4">
                    <div className="flex h-5 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="flex items-center justify-center bg-intellend-green text-xs font-semibold text-white transition-all"
                        style={{
                          width: `${getPaidPrincipalPercentage(details.loan_details)}%`,
                        }}
                      >
                        {Math.round(
                          getPaidPrincipalPercentage(details.loan_details),
                        ) >= 12
                          ? `${Math.round(getPaidPrincipalPercentage(details.loan_details))}%`
                          : ""}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block h-2.5 w-2.5 rounded-sm bg-intellend-green" />
                        Paid
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-200" />
                        Outstanding
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="my-3 border-t border-gray-100" />

                  {/* Tenure */}
                  <div>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-1.5 text-gray-500">Tenure</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">
                            {details.loan_details.tenure}{" "}
                            {getTenureUnitLabel(
                              details.loan_details.payment_freq,
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-1.5 text-gray-500">
                            Installments Paid
                          </td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">
                            {details.loan_details.installment_paid_cnt}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-1.5 text-gray-500">
                            Installments Left
                          </td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">
                            {details.loan_details.installment_left_cnt}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Payment Status */}
              {isOverDue(details.loan_details) ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                  <div className="flex items-center justify-between gap-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <AlertCircle className="h-5 w-5" />
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-red-900">
                          Payment Overdue
                        </h3>
                      </div>
                    </div>

                    <div className="min-w-[120px] text-right">
                      <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
                        Days Overdue
                      </p>

                      <p className="mt-1 text-3xl font-bold leading-none text-red-700">
                        {details.loan_details.days_past_due}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 border-t border-red-200 pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-red-800">
                        Total Due
                      </span>

                      <span className="text-xl font-bold text-red-700">
                        {formatCurrency(
                          details.loan_details.total_overdue_amount,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-green-200 bg-green-50 p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                        <CheckCircle2 className="h-5 w-5" />
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-green-900">
                          Payments Up to Date
                        </h3>

                        <p className="mt-1 text-sm text-green-700">
                          Your payments are up to date. No overdue amount is
                          currently due.
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-xs font-semibold uppercase tracking-wide text-green-600">
                        Days Overdue
                      </p>

                      <p className="mt-1 text-2xl font-bold text-green-700">
                        0
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Support */}
              <div className="mt-6 flex flex-col gap-3">
                {/* Row 1: Foreclosure + Repayment Schedule side by side */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="flex items-center justify-center rounded-lg border border-intellend-green px-3 py-2.5 text-sm font-semibold text-intellend-green transition hover:bg-intellend-green hover:text-white focus:outline-none focus:ring-2 focus:ring-intellend-green focus:ring-offset-2"
                  >
                    Foreclosure
                  </button>
                  <button
                    type="button"
                    className="flex items-center justify-center rounded-lg border border-intellend-green px-3 py-2.5 text-sm font-semibold text-intellend-green transition hover:bg-intellend-green hover:text-white focus:outline-none focus:ring-2 focus:ring-intellend-green focus:ring-offset-2"
                  >
                    Repayment Schedule
                  </button>
                </div>

                {/* Row 2: Need Help? full width */}
                <button
                  type="button"
                  onClick={handleRequestSupport}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-intellend-green px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200"
                  disabled={isSupportRequesting}
                >
                  <Phone className="h-4 w-4" />
                  Need Help?
                </button>

                {supportMessage && (
                  <p
                    className={`text-center text-sm ${
                      isSupportSuccess ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {supportMessage}
                  </p>
                )}
              </div>
            </div>
          </main>

          {/* Footer */}
          <footer className="border-t border-gray-200 bg-white">
            <div className="mx-auto w-full max-w-5xl px-6 py-4">
              <button
                type="button"
                onClick={logoutUser}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </footer>
        </div>
      )}

      {currentStep == Step.NoDetails && (
        <div className="flex min-h-screen flex-col bg-gray-50">
          {/* Header */}
          <header className="border-b border-gray-200 bg-white">
            <div className="mx-auto flex h-16 w-full max-w-5xl items-center justify-between px-6">
              <h1 className="text-lg font-semibold text-gray-900">
                Loan Details
              </h1>

              <button
                type="button"
                onClick={logoutUser}
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-red-600"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </header>

          {/* Content */}
          <main className="flex flex-1 items-center justify-center px-6 py-12">
            <div className="w-full max-w-md text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                <AlertCircle className="h-7 w-7" />
              </div>

              <h2 className="mt-5 text-xl font-semibold text-gray-900">
                {errorMessage}
              </h2>

              {/* <p className="mt-2 text-sm leading-6 text-gray-500">"message"</p> */}

              <button
                type="button"
                onClick={fetchLoanDetails}
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-intellend-green px-5 py-2.5 text-sm font-semibold text-white transition"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          </main>
        </div>
      )}
    </div>
  );
};

export default BasicLoanDetailsPage;
